"use client";
import React, { useState, useRef, useEffect } from "react";
import { bootcamp_programData, tabs } from "@/constants";
import { Program } from "@/interfaces/Bootcamp";
import ProgramDetailModal from "./ProgramDetailModal";

const ProgramCarousel = () => {
  const [activeTab, setActiveTab] = useState(tabs[0] || ""); // default tab pertama
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const programsToDisplay: Program[] = bootcamp_programData[activeTab] || [];

  const getBackgroundImage = () => {
    if (activeTab === "Bahasa Jepang") {
      return "url('/img/bg-japanese-carousel.jpg')";
    }
    if (activeTab === "Pemograman IT") {
      return "url('/img/bg-fullstack-carousel.jpg')";
    }
    return "url('/img/bg-bootcamp-carousel.jpg')";
  };

  const updateScrollPosition = () => {
    const container = scrollRef.current;
    if (container) {
      setIsAtStart(container.scrollLeft <= 0);
      setIsAtEnd(
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 1
      );
    }
  };

  const getCardWidth = () => {
    const container = scrollRef.current;
    const card = container?.querySelector(".card") as HTMLElement;
    return card ? card.clientWidth + 16 : 356;
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    const cardWidth = getCardWidth();
    const scrollAmount = window.innerWidth <= 768 ? cardWidth : cardWidth * 3;
    if (container) {
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
      setTimeout(updateScrollPosition, 300);
    }
  };

  useEffect(() => {
    updateScrollPosition();
    const container = scrollRef.current;
    container?.addEventListener("scroll", updateScrollPosition);
    return () => container?.removeEventListener("scroll", updateScrollPosition);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ left: 0, behavior: "auto" });
    updateScrollPosition();
  }, [activeTab]);

  const showArrows = programsToDisplay.length >= 3;

  const rerouteToWhatsApp = () => {
    const phoneNumber = "6281234567890";
    const message = "Halo, saya ingin custom sesuai kebutuhan 😊";

    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <section
      className="relative px-6 py-12 text-white bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: getBackgroundImage() }}
    >
      <div className="absolute inset-0 bg-[#0C1E38] opacity-70 z-0" />

      <div className="relative z-10">
        <div className="flex flex-col mx-auto text-center -mt-3 mb-4 md:mb-5 items-center">
          <h2 className="text-2xl font-body font-bold align-center sm:text-4xl leading-relaxed">
            Bootcamp
          </h2>
          <p className="text-base font-body font-extralight align-center mt-1 md:mt-2 sm:text-lg px-3">
            Tentukan Program Sesuai Skill Level & Tujuanmu
          </p>
          <div className="border-2 md:border-4 border-accent-100 rounded-full w-40 mt-3 md:mt-3" />
        </div>

        <div className="flex justify-center gap-4 mb-6 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded font-medium ${activeTab === tab
                ? "bg-teal-500 text-white hover:bg-teal-500"
                : "bg-white text-gray-700 hover:bg-teal-100"
                }`}
            >
              {tab}
            </button>
          ))}
          <button
            onClick={() => rerouteToWhatsApp()}
            className={`px-4 py-2 rounded font-medium bg-white text-gray-700 hover:bg-teal-100`}
          >
            Custom Sesuai Kebutuhan
          </button>
        </div>

        <div className="relative mx-auto max-w-[1080px]">
          {showArrows && !isAtStart && (
            <button
              onClick={() => scroll("left")}
              className="absolute -left-5 top-1/2 -translate-y-1/2 z-20 bg-teal-300 text-black p-2 rounded-full shadow-xl hover:bg-teal-500 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          <div className="overflow-hidden">
            <div
              ref={scrollRef}
              className="grid auto-cols-[340px] grid-flow-col overflow-x-auto scroll-smooth scrollbar-hide mx-auto gap-4"
              style={{
                width: programsToDisplay.length <= 2 ? "fit-content" : "100%",
                margin: programsToDisplay.length <= 2 ? "auto" : "0",
              }}
            >
              {programsToDisplay.map((program, index) => (
                <div
                  key={`${activeTab}-${index}`}
                  className="w-[340px] flex-shrink-0 bg-white text-black shadow-md card flex flex-col"
                >
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4 flex flex-col flex-grow">
                    <h3 className="text-gray-500 text-sm">{activeTab}</h3>
                    <h3 className="font-semibold text-lg">{program.title}</h3>
                    <p className="text-sm text-gray-600">{program.duration}</p>

                    <div className="flex flex-col flex-grow">
                      {program.tags && program.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {program.tags.map((tag, idx) => (
                            <span
                              key={`tag-${idx}`}
                              className="bg-gray-200 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-red-600 font-bold mt-auto pt-2">
                        {program.price}
                      </p>
                    </div>

                    <div className="flex justify-between mt-2">
                      <button
                        onClick={() => setSelectedProgram(program)}
                        className="text-[#448ada] text-base font-bold hover:underline"
                      >
                        Lihat Detail
                      </button>
                      <a
                        href="https://wa.me/6281215636018"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-teal-600 text-white px-6 py-1 rounded text-base hover:bg-black duration-300 hover:text-white font-bold"
                      >
                        Daftar
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showArrows && !isAtEnd && (
            <button
              onClick={() => scroll("right")}
              className="absolute -right-5 top-1/2 -translate-y-1/2 z-20 bg-teal-300 text-black p-2 rounded-full shadow-xl hover:bg-teal-500 transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>

        <ProgramDetailModal
          program={selectedProgram}
          onClose={() => setSelectedProgram(null)}
        />
      </div>
    </section>
  );
};

export default ProgramCarousel;
